// 🌿 API Craft Back Office - CRUD Hook
import { useState, useEffect } from 'react';
import { DynamicRecord, ApiResponse, CreateRecordRequest, UpdateRecordRequest } from '../types';
import { crudApi } from '../services/api';

export const useCrud = (modelName: string) => {
  const [records, setRecords] = useState<DynamicRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchRecords = async (
    page: number = 1, 
    pageSize: number = 10, 
    expand?: string
  ) => {
    if (!modelName) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`🔄 Fetching ${modelName} records (page ${page}, size ${pageSize})...`);
      const response: ApiResponse<DynamicRecord[]> = await crudApi.getRecords(
        modelName,
        page,
        pageSize,
        expand
      );
      
      setRecords(response.data);
      setPagination({
        page: response.page || page,
        pageSize: response.page_size || pageSize,
        total: response.total || 0,
      });
      
      console.log(`✅ Fetched ${response.data.length} ${modelName} records`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || `Failed to fetch ${modelName} records`;
      setError(errorMessage);
      console.error(`🚨 Error fetching ${modelName} records:`, errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const createRecord = async (data: CreateRecordRequest): Promise<DynamicRecord> => {
    setError(null);
    try {
      console.log(`🔄 Creating ${modelName} record...`, data);
      const newRecord = await crudApi.createRecord(modelName, data);
      
      // Refresh current page to show new record
      await fetchRecords(pagination.page, pagination.pageSize);
      
      console.log(`✅ Created ${modelName} record:`, newRecord.id);
      return newRecord;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || `Failed to create ${modelName} record`;
      setError(errorMessage);
      console.error(`🚨 Error creating ${modelName} record:`, errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateRecord = async (
    id: string, 
    data: UpdateRecordRequest
  ): Promise<DynamicRecord> => {
    setError(null);
    try {
      console.log(`🔄 Updating ${modelName} record ${id}...`, data);
      const updatedRecord = await crudApi.updateRecord(modelName, id, data);
      
      // Refresh current page to show updated record
      await fetchRecords(pagination.page, pagination.pageSize);
      
      console.log(`✅ Updated ${modelName} record:`, updatedRecord.id);
      return updatedRecord;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || `Failed to update ${modelName} record`;
      setError(errorMessage);
      console.error(`🚨 Error updating ${modelName} record:`, errorMessage);
      throw new Error(errorMessage);
    }
  };

  const deleteRecord = async (id: string): Promise<void> => {
    setError(null);
    try {
      console.log(`🔄 Deleting ${modelName} record ${id}...`);
      await crudApi.deleteRecord(modelName, id);
      
      // Refresh current page
      await fetchRecords(pagination.page, pagination.pageSize);
      
      console.log(`✅ Deleted ${modelName} record: ${id}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || `Failed to delete ${modelName} record`;
      setError(errorMessage);
      console.error(`🚨 Error deleting ${modelName} record:`, errorMessage);
      throw new Error(errorMessage);
    }
  };

  const getRecord = async (id: string, expand?: string): Promise<DynamicRecord> => {
    setError(null);
    try {
      console.log(`🔄 Fetching ${modelName} record ${id}...`);
      const record = await crudApi.getRecord(modelName, id, expand);
      console.log(`✅ Fetched ${modelName} record:`, record.id);
      return record;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || `Failed to fetch ${modelName} record`;
      setError(errorMessage);
      console.error(`🚨 Error fetching ${modelName} record:`, errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Load initial data when modelName changes
  useEffect(() => {
    if (modelName) {
      fetchRecords();
    }
  }, [modelName]);

  const changePage = (newPage: number) => {
    console.log(`📄 Changing to page ${newPage}`);
    fetchRecords(newPage, pagination.pageSize);
  };

  const changePageSize = (newPageSize: number) => {
    console.log(`📏 Changing page size to ${newPageSize}`);
    fetchRecords(1, newPageSize); // Reset to first page with new size
  };

  const refresh = () => {
    console.log(`🔄 Refreshing ${modelName} records...`);
    fetchRecords(pagination.page, pagination.pageSize);
  };

  // Search records (local filtering for now)
  const searchRecords = (query: string): DynamicRecord[] => {
    if (!query.trim()) return records;
    
    return records.filter(record =>
      Object.values(record).some(value =>
        String(value).toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  // Get records statistics
  const getRecordsStats = () => {
    return {
      total: pagination.total,
      currentPage: pagination.page,
      pageSize: pagination.pageSize,
      totalPages: Math.ceil(pagination.total / pagination.pageSize),
      hasNextPage: pagination.page < Math.ceil(pagination.total / pagination.pageSize),
      hasPreviousPage: pagination.page > 1,
    };
  };

  return {
    records,
    isLoading,
    error,
    pagination,
    createRecord,
    updateRecord,
    deleteRecord,
    getRecord,
    fetchRecords,
    changePage,
    changePageSize,
    refresh,
    searchRecords,
    getRecordsStats,
  };
};
